import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  styled,
  type SxProps,
  type Theme,
} from "~/common/mui";
import type {
  ConfiguratorPreviewSectionViewModel,
  ConfiguratorPreviewViewModel,
} from "./buildConfiguratorPreviewViewModel";

export interface ConfiguratorPreviewProps {
  viewModel: ConfiguratorPreviewViewModel;
  className?: string;
  sx?: SxProps<Theme>;
}

export interface ConfiguratorPreviewImageProps {
  imageUrl?: string;
  designation?: string;
}

interface TechnicalOverviewSectionProps {
  section: ConfiguratorPreviewSectionViewModel;
}

const PreviewCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "none",
  backgroundColor: theme.palette.surfaceContainer.lowest,
}));

const PreviewImageArea = styled(Box)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  aspectRatio: "4 / 3",
  padding: theme.spacing(1),
  backgroundColor: theme.palette.surfaceContainer.lowest,
}));

const PreviewImage = styled("img")({
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const PreviewImagePlaceholder = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: theme.palette.surfaceContainer.low,
}));

const ExpandImageButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.surfaceContainer.lowest,
  boxShadow: theme.shadows[1],
  "&:hover": {
    backgroundColor: theme.palette.surfaceContainer.low,
  },
}));

const TechnicalOverview = styled(Stack)(({ theme }) => ({
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(0, 2, 2),
}));

const OverviewRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "striped",
})<{ striped: boolean }>(({ striped, theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  backgroundColor: striped ? theme.palette.surfaceContainer.low : theme.palette.surfaceContainer.lowest,
}));

const OverviewCell = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  padding: theme.spacing(1),
  overflowWrap: "anywhere",
}));

const ExpandedImage = styled("img")({
  display: "block",
  width: "100%",
  maxHeight: "80vh",
  objectFit: "contain",
});

export const ConfiguratorPreviewImage = ({ imageUrl, designation }: ConfiguratorPreviewImageProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const imageTitle = designation ?? t("feature.configurator.v2.preview.image", "Configuration preview");

  return (
    <>
      <PreviewImageArea>
        {imageUrl ? (
          <>
            <PreviewImage src={imageUrl} alt={imageTitle} />
            <ExpandImageButton
              aria-label={t("feature.configurator.v2.preview.expandImage", "Expand configuration preview")}
              onClick={() => setExpanded(true)}
            >
              <OpenInFullRoundedIcon />
            </ExpandImageButton>
          </>
        ) : (
          <PreviewImagePlaceholder aria-hidden />
        )}
      </PreviewImageArea>

      {imageUrl && (
        <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth="desktop-l" fullWidth>
          <DialogHeader onClose={() => setExpanded(false)}>
            <DialogTitle>{imageTitle}</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <ExpandedImage src={imageUrl} alt={imageTitle} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const TechnicalOverviewSection = ({ section }: TechnicalOverviewSectionProps) => (
  <Stack gap={0.5}>
    <Typography variant="bodyLarge" sx={{ fontWeight: 700 }}>
      {section.title}
    </Typography>
    <Box>
      {section.rows.map((row, index) => (
        <OverviewRow key={row.key} striped={index % 2 === 0}>
          <OverviewCell variant="bodySmall">{row.label}</OverviewCell>
          <OverviewCell variant="bodySmall">{row.value}</OverviewCell>
        </OverviewRow>
      ))}
    </Box>
  </Stack>
);

export const ConfiguratorPreview = ({ viewModel, className, sx }: ConfiguratorPreviewProps) => (
  <PreviewCard className={className} sx={sx}>
    <ConfiguratorPreviewImage imageUrl={viewModel.imageUrl} designation={viewModel.designation} />
    <Divider sx={{ my: 2 }} />
    <TechnicalOverview data-configurator-scroll-region="summary" gap={2}>
      {viewModel.designation && (
        <Typography variant="titleLarge" sx={{ fontWeight: 700, mb: 1 }}>
          {viewModel.designation}
        </Typography>
      )}
      {viewModel.sections.map((section) => (
        <TechnicalOverviewSection key={section.key} section={section} />
      ))}
    </TechnicalOverview>
  </PreviewCard>
);
